import React from 'react';
import {
  ChevronRight,
  ImageOff,
  Video,
  Image as ImageIcon,
  Repeat,
  CalendarClock,
  MoreVertical,
  Trash2,
} from 'lucide-react';
import { Badge } from '../ui';
import { getDeviceDisplayName, getResolutionLabel, getLastSeen } from '../../utils/deviceLabel';

/**
 * ScreenRow — compact row for a single TV in the Screens list.
 * Inspired by Linear/Vercel/Stripe admin list density, with a thumbnail
 * of the currently-playing content, loop progress bar, and status badge.
 */
function formatLastSeen(timestamp) {
  if (!timestamp) return 'Never seen';
  const ts = typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp;
  const diff = Date.now() - ts;
  if (Number.isNaN(diff)) return '—';
  const s = Math.floor(diff / 1000);
  if (s < 10)       return 'Just now';
  if (s < 60)       return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)       return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)       return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30)       return `${d}d ago`;
  return 'Long ago';
}

function statusVariant(status) {
  if (status === 'online') return 'online';
  if (status === 'pending') return 'pending';
  return 'offline';
}

function statusLabel(status) {
  if (status === 'online')  return 'Live';
  if (status === 'pending') return 'Pending';
  return 'Offline';
}

/**
 * Compute how far through a looping slot the screen is right now.
 * Returns { progress: 0..1, loopText: '3 of 5' } or null.
 */
function computeLoopProgress(np, serverTime) {
  if (!np) return null;
  const start = np.slotStartTime ? new Date(np.slotStartTime).getTime() : null;
  const end   = np.slotEndTime   ? new Date(np.slotEndTime).getTime()   : null;
  const now   = serverTime ? new Date(serverTime).getTime() : Date.now();
  if (!start || !end || end <= start) return null;
  const raw = (now - start) / (end - start);
  const progress = Math.max(0, Math.min(1, raw));
  const loopText =
    np.isLoop && np.loopTotal
      ? `${(np.loopIndex ?? 0) + 1} of ${np.loopTotal}`
      : null;
  return { progress, loopText };
}

const ScreenRow = ({
  device,
  selected,
  onClick,
  nowPlaying,
  serverTime,
  timelineOpen = false,
  onToggleTimeline,
  onRequestDelete,
}) => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef(null);
  React.useEffect(() => {
    if (!menuOpen) return undefined;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);
  const name = getDeviceDisplayName(device);
  const resolution = getResolutionLabel(device);
  const isOffline = device.status !== 'online';

  const np = nowPlaying || null;
  const loopInfo = computeLoopProgress(np, serverTime);
  const mediaIcon =
    np?.mediaType === 'video' ? <Video size={10} /> :
    np?.mediaType === 'image' ? <ImageIcon size={10} /> :
    null;

  return (
    <div
      className={`screen-row ${selected ? 'screen-row--selected' : ''}`}
      role="button"
      tabIndex={0}
      aria-selected={selected ? 'true' : 'false'}
      onClick={() => onClick?.(device)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(device);
        }
      }}
    >
      {/* Thumbnail of currently-playing content */}
      <div
        className={[
          'screen-row__thumb',
          !np?.thumbnailUrl && 'screen-row__thumb--empty',
          isOffline && 'screen-row__thumb--offline',
        ].filter(Boolean).join(' ')}
      >
        {np?.thumbnailUrl ? (
          <img src={np.thumbnailUrl} alt="" loading="lazy" />
        ) : (
          <ImageOff size={14} />
        )}
      </div>

      {/* Name + subtitle */}
      <div className="screen-row__name-cell">
        <div className="screen-row__name" title={name}>
          {name}
        </div>
        <div className="screen-row__subtitle" title={resolution || device.deviceId || ''}>
          {resolution || device.model || device.deviceId || '—'}
        </div>
      </div>

      {/* Now playing */}
      <div className="screen-row__playing">
        {np?.title ? (
          <>
            <div className="screen-row__playing-title" title={np.title}>
              {mediaIcon}
              {np.title}
            </div>
            {loopInfo && (
              <>
                <div className="screen-row__progress" aria-hidden="true">
                  <div
                    className="screen-row__progress-bar"
                    style={{ width: `${Math.round(loopInfo.progress * 100)}%` }}
                  />
                </div>
                {loopInfo.loopText && (
                  <span className="screen-row__loop-meta">
                    <Repeat size={9} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    {loopInfo.loopText}
                  </span>
                )}
              </>
            )}
          </>
        ) : (
          <div className="screen-row__playing-title screen-row__playing-title--empty">
            {isOffline ? 'Not broadcasting' : 'Screensaver'}
          </div>
        )}
        {/* The playback-health pill moved to the internal Watchdog Console.
            Online/offline stays here because it is a client-meaningful fact;
            "frozen", "wrong content" and stall counts are diagnostics. */}
      </div>

      {/* Last seen */}
      <div className="screen-row__meta">{formatLastSeen(getLastSeen(device))}</div>

      {/* Status badge */}
      <div className="screen-row__status">
        <Badge variant={statusVariant(device.status)} dot size="sm">
          {statusLabel(device.status)}
        </Badge>
      </div>

      {/* Schedule timeline editor lives on the Dashboard tab (click a row in
          "Today's Timeline"). This tab keeps its existing row-click → TVDetails
          side-panel behaviour untouched. */}
      <div className="screen-row__actions" ref={menuRef}>
        {onRequestDelete && (
          <>
            <button
              type="button"
              className="screen-row__menu-btn"
              aria-label="Screen actions"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
            >
              <MoreVertical size={16} />
            </button>
            {menuOpen && (
              <div className="screen-row__menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="screen-row__menu-item screen-row__menu-item--danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    onRequestDelete(device);
                  }}
                >
                  <Trash2 size={14} /> Delete screen…
                </button>
              </div>
            )}
          </>
        )}
        <div className="screen-row__chevron" aria-hidden="true">
          <ChevronRight size={16} />
        </div>
      </div>
    </div>
  );
};

export default ScreenRow;
